import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Code2, Search, Upload, LogOut, LayoutDashboard, User, Moon, Sun, TrendingUp, Settings, ArrowLeft, Info, LogIn, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthModal } from "@/hooks/useAuthModal";
import { useAuth } from "@/hooks/useAuth";
import macroHubLogo from "@/assets/macrohub-logo.png";
import { Separator } from "@/components/ui/separator";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useIsMobile } from "@/hooks/use-mobile";
interface HeaderProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onSearchSubmit?: () => void;
  showSearch?: boolean;
  variant?: "default" | "landing";
  onBackClick?: () => void;
  onNavigate?: (path: string) => void;
}
export const Header = ({
  searchQuery = "",
  onSearchChange,
  onSearchSubmit,
  showSearch = false,
  variant = "default",
  onBackClick,
  onNavigate
}: HeaderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    openAuthModal
  } = useAuthModal();
  const {
    user,
    profile,
    isLoading: authLoading
  } = useAuth();
  const [searchFocused, setSearchFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<Array<{
    name: string;
    slug: string;
  }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showHeaderCTA, setShowHeaderCTA] = useState(false);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const {
    theme,
    setTheme
  } = useTheme();
  const isMobile = useIsMobile();
  useEffect(() => {
    // Fetch popular macros for suggestions
    fetchSuggestions();
  }, []);
  useEffect(() => {
    if (variant === "landing") {
      const handleScroll = () => {
        // Show header CTA when hero "Browse for free" button scrolls out of view (~400px)
        setShowHeaderCTA(window.scrollY > 400);
      };
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [variant]);
  const fetchSuggestions = async () => {
    const {
      data
    } = await supabase.from("macros").select("name, slug").eq("is_published", true).order("views_count", {
      ascending: false
    }).limit(5);
    if (data) {
      setSuggestions(data);
    }
  };
  const getInitials = (name: string) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };
  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };
  const handleSuggestionClick = (macroName: string) => {
    setShowSuggestions(false);
    setSearchFocused(false);

    // If we're on a macro detail page, navigate to vault with search
    if (location.pathname.startsWith('/m/')) {
      navigate(`/vault?search=${encodeURIComponent(macroName)}`);
      return;
    }

    // Otherwise, update search query and submit if handler exists
    if (onSearchChange) {
      onSearchChange(macroName);
    }
    if (onSearchSubmit) {
      setTimeout(() => onSearchSubmit(), 0);
    }
  };
  // Landing page variant - minimal header
  if (variant === "landing") {
    return <header className="fixed top-0 inset-x-0 z-50 py-3 md:py-6">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="backdrop-blur-md rounded-full px-8 py-4 flex items-center justify-between border border-border/50 opacity-97.5 bg-muted">
            <Link to="/" className="flex items-center gap-3">
              <img src={macroHubLogo} alt="MacroVault Logo" className="h-8 w-8" />
              <span className="text-xl font-bold hidden sm:inline">MacroVault</span>
            </Link>

            <nav className="flex items-center gap-4">
              <Link to="/auth?mode=login" className="text-base font-medium hover:text-primary transition-colors">
                Log in
              </Link>
              {showHeaderCTA && <Button size="sm" onClick={() => navigate("/vault")} className="rounded-full px-6 animate-fade-in bg-green-700 hover:bg-green-600">
                  Browse for free 
                </Button>}
            </nav>
          </div>
        </div>
      </header>;
  }
  return <>
      {/* Overlay */}
      {searchFocused && showSearch && <div className="fixed inset-0 bg-black/10 z-40 transition-opacity duration-200" onClick={() => setSearchFocused(false)} />}

      <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-200 bg-background`}>
        <div className="px-6 md:px-8 lg:px-12">
          <div className="flex h-16 md:h-20 items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Link to="/vault" className="hover:opacity-90 transition-opacity shrink-0" onClick={e => {
              // Clear any stored navigation state
              sessionStorage.removeItem('clickedMacroSlug');
              if (location.pathname === '/vault') {
                e.preventDefault();
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              } else if (onNavigate) {
                e.preventDefault();
                onNavigate('/vault');
              }
            }}>
                  <div className="flex items-center gap-3">
                  <img src={macroHubLogo} alt="MacroHub Logo" className="h-10 w-10" />
                  <div className="flex flex-col">
                    <span className={`text-2xl font-bold hidden md:inline ${location.pathname === '/upload' ? 'md:hidden' : ''}`}>MacroVault</span>
                  </div>
                </div>
              </Link>

              {location.pathname === '/upload' && <button onClick={() => {
              if (onBackClick) {
                onBackClick();
              } else {
                navigate(-1);
              }
            }} className="ml-2 inline-flex items-center gap-2 text-base font-medium hover:text-primary transition-colors">
                  <ArrowLeft className="h-5 w-5" />
                  <span className="hidden sm:inline">Back</span>
                </button>}
            </div>

            {showSearch && onSearchChange && <div className={`flex-1 mx-2 md:mx-4 transition-all duration-200 ${searchFocused ? "max-w-3xl" : "max-w-2xl"}`}>
                <div className="relative z-50">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input placeholder={!user && isMobile ? "Search..." : "Search macros..."} value={searchQuery} onChange={e => onSearchChange(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && onSearchSubmit) {
                  onSearchSubmit();
                }
              }} onFocus={() => {
                setSearchFocused(true);
                setShowSuggestions(true);
              }} onBlur={() => {
                setSearchFocused(false);
                setTimeout(() => setShowSuggestions(false), 200);
              }} className={`pl-12 ${searchQuery ? 'pr-12' : 'pr-4'} h-12 text-base rounded-full transition-all duration-200 ${searchFocused ? "border-0 bg-background shadow-[0_0_20px_rgba(0,0,0,0.1),inset_0_2px_4px_rgba(0,0,0,0.05)] ring-2 ring-primary/20" : "border border-border/50 bg-muted focus-visible:ring-0"}`} />
                  
                  {searchQuery && <button onMouseDown={e => e.preventDefault()} onClick={() => onSearchChange('')} className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground hover:text-foreground transition-colors" aria-label="Clear search">
                      <X className="h-5 w-5" />
                    </button>}

                  {/* Search Suggestions Dropdown */}
                  {showSuggestions && suggestions.length > 0 && <div className="absolute top-full mt-2 w-full bg-card border border-border rounded-2xl shadow-lg overflow-hidden z-50 animate-fade-in">
                      <div className="p-2">
                        <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground">
                          <TrendingUp className="h-4 w-4" />
                          Popular Searches
                        </div>
                        {suggestions.map(suggestion => <button key={suggestion.slug} onClick={() => handleSuggestionClick(suggestion.name)} className="w-full text-left px-3 py-3 rounded-lg hover:bg-accent transition-colors flex items-center gap-3 group">
                            <Search className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                            <span className="text-base font-medium">{suggestion.name}</span>
                          </button>)}
                      </div>
                    </div>}
                </div>
              </div>}

            <nav className="flex items-center gap-3 md:gap-6 shrink-0">
              <button data-onboarding-upload className="hidden md:flex items-center gap-2 text-base font-medium hover:text-primary cursor-pointer" onClick={e => {
              if (!user) {
                e.preventDefault();
                openAuthModal(() => {
                  navigate('/upload');
                });
                return;
              }
              const onboardingStep = sessionStorage.getItem('continueOnboardingStep');
              if (onboardingStep === '4') {
                localStorage.setItem('hasCompletedOnboarding', 'true');
                sessionStorage.removeItem('continueOnboardingStep');
              }
              if (location.pathname === '/upload') {
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              } else if (onNavigate) {
                onNavigate('/upload');
              } else {
                navigate('/upload');
              }
            }}>
                <Upload className="h-5 w-5" />
                <span>Upload</span>
              </button>

              {/* Profile Avatar Dropdown or Sign In Button */}
              {authLoading ? <div className="w-10 h-10 rounded-full bg-muted animate-pulse" /> : profile && user ? <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="focus:outline-none" data-onboarding-profile>
                          {profile.avatar_url ? <img src={profile.avatar_url} alt={profile.username} className="w-10 h-10 rounded-full object-cover cursor-pointer" /> : <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-sm font-bold text-primary-foreground cursor-pointer">
                              {getInitials(profile.full_name || profile.username)}
                            </div>}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52 rounded-xl shadow-lg border bg-card animate-fade-in p-2">
                        <DropdownMenuItem asChild className="rounded-lg cursor-pointer py-3 px-3 hover:bg-accent transition-colors">
                          <Link to={`/profile/${profile.username}`} className="flex items-center" onClick={e => {
                    if (onNavigate) {
                      e.preventDefault();
                      onNavigate(`/profile/${profile.username}`);
                    }
                  }}>
                            <User className="h-5 w-5 mr-3 text-muted-foreground" />
                            <span className="font-medium text-base">Profile</span>
                          </Link>
                        </DropdownMenuItem>

                        <DropdownMenuItem asChild className="rounded-lg cursor-pointer py-3 px-3 hover:bg-accent transition-colors">
                          <Link to="/dashboard" className="flex items-center" onClick={e => {
                    if (onNavigate) {
                      e.preventDefault();
                      onNavigate('/dashboard');
                    }
                  }}>
                            <LayoutDashboard className="h-5 w-5 mr-3 text-muted-foreground" />
                            <span className="font-medium text-base">Dashboard</span>
                          </Link>
                        </DropdownMenuItem>

                        <DropdownMenuItem asChild className="rounded-lg cursor-pointer py-3 px-3 hover:bg-accent transition-colors">
                          <Link to="/settings/account" className="flex items-center" onClick={e => {
                    if (onNavigate) {
                      e.preventDefault();
                      onNavigate('/settings/account');
                    }
                  }}>
                            <Settings className="h-5 w-5 mr-3 text-muted-foreground" />
                            <span className="font-medium text-base">Settings</span>
                          </Link>
                        </DropdownMenuItem>

                        <DropdownMenuItem asChild className="rounded-lg cursor-pointer py-3 px-3 hover:bg-accent transition-colors md:hidden">
                          <button className="flex items-center w-full" onClick={e => {
                    if (!user) {
                      e.preventDefault();
                      openAuthModal(() => {
                        navigate('/upload');
                      });
                      return;
                    }
                    if (location.pathname === '/upload') {
                      window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                      });
                    } else if (onNavigate) {
                      onNavigate('/upload');
                    } else {
                      navigate('/upload');
                    }
                  }}>
                            <Upload className="h-5 w-5 mr-3 text-muted-foreground" />
                            <span className="font-medium text-base">Upload</span>
                          </button>
                        </DropdownMenuItem>

                        <Separator className="my-2" />

                        <DropdownMenuItem asChild className="rounded-lg cursor-pointer py-3 px-3 hover:bg-accent transition-colors">
                          <Link to="/docs" className="flex items-center" onClick={e => {
                    if (onNavigate) {
                      e.preventDefault();
                      onNavigate('/docs');
                    }
                  }}>
                            <Info className="h-5 w-5 mr-3 text-muted-foreground" />
                            <span className="font-medium text-base">Guide</span>
                          </Link>
                        </DropdownMenuItem>

                        <DropdownMenuItem onClick={toggleTheme} className="rounded-lg cursor-pointer py-3 px-3 hover:bg-accent transition-colors">
                          {theme === "dark" ? <Sun className="h-5 w-5 mr-3 text-muted-foreground" /> : <Moon className="h-5 w-5 mr-3 text-muted-foreground" />}
                          <span className="font-medium text-base">{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem onClick={() => setShowSignOutDialog(true)} className="rounded-lg cursor-pointer py-3 px-3 hover:bg-destructive/10 text-destructive transition-colors">
                          <LogOut className="h-5 w-5 mr-3" />
                          <span className="font-medium text-base">Sign Out</span>
                        </DropdownMenuItem>

                        <Separator className="my-2" />

                        {/* Footer Links */}
                        <div className="px-3 py-2 flex items-center gap-2 text-xs text-muted-foreground">
                          <Link to="/privacy" className="hover:text-foreground transition-colors">
                            Privacy
                          </Link>
                          <Link to="/terms" className="hover:text-foreground transition-colors">
                            Terms
                          </Link>
                          
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu> : <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="focus:outline-none">
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors">
                            <User className="h-5 w-5 text-muted-foreground" />
                          </div>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52 rounded-xl shadow-lg border bg-card p-2">
                        {/* Primary Sign In Action */}
                        <DropdownMenuItem asChild className="rounded-lg cursor-pointer py-3 px-3 hover:bg-accent transition-colors">
                          <Link to="/auth" className="flex items-center">
                            <LogIn className="h-5 w-5 mr-3 text-muted-foreground" />
                            <span className="font-medium text-base">Sign In / Join </span>
                          </Link>
                        </DropdownMenuItem>

                        <Separator className="my-2" />

                        {/* Guide Link */}
                        <DropdownMenuItem asChild className="rounded-lg cursor-pointer py-3 px-3 hover:bg-accent transition-colors">
                          <Link to="/docs" className="flex items-center">
                            <Info className="h-5 w-5 mr-3 text-muted-foreground" />
                            <span className="font-medium text-base">Guide</span>
                          </Link>
                        </DropdownMenuItem>

                        <Separator className="my-2" />

                        {/* Footer Links */}
                        <div className="px-3 py-2 flex items-center gap-2 text-xs text-muted-foreground">
                          <Link to="/privacy" className="hover:text-foreground transition-colors">
                            Privacy
                          </Link>
                          <Link to="/terms" className="hover:text-foreground transition-colors">
                            Terms
                          </Link>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>}
            </nav>
          </div>
        </div>
      </header>

      {/* Sign Out Confirmation Dialog */}
      <AlertDialog open={showSignOutDialog} onOpenChange={setShowSignOutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign Out?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign out? You'll need to log in again to access your dashboard and saved macros.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
            handleSignOut();
            setShowSignOutDialog(false);
          }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>;
};